import { useEffect, useMemo, useRef, useState } from 'react'
import type { MarketModel, SimInputs, SimResult } from '../engine/types'
import { FULL_PATHS, PREVIEW_PATHS, SOLO_KEY } from '../worker/protocol'
import type { SimKey, SimRequest, SimResponse } from '../worker/protocol'

/**
 * Fixed, not random. Every visitor running the same inputs sees the same chart,
 * so a shared link reproduces its sender's screenshot exactly and nobody opens a
 * thread asking why their number moved.
 *
 * It is also why two scenarios can be compared at all: they run on the same seed,
 * so the difference between them is their inputs and not Monte Carlo noise.
 */
export const SHARED_SEED = 0x5eed

export interface SimulationState {
  result: SimResult | null
  /** True while showing the fast tier — the fan is still settling. */
  isPreview: boolean
  /** Worker time for the last run. Surfaced on /dev, not in the product UI. */
  elapsedMs: number
  error: string | null
}

const INITIAL: SimulationState = {
  result: null,
  isPreview: true,
  elapsedMs: 0,
  error: null,
}

export interface SimEntry {
  key: SimKey
  inputs: SimInputs
  model: MarketModel
}

interface Options {
  /**
   * True while a control is actively being dragged. Drives the fidelity tier:
   * cheap runs during the gesture, the full 10,000 paths once the thumb lifts.
   */
  interacting?: boolean
  seed?: number
}

type WorkerMessage = SimResponse | { key: SimKey; generation: number; error: string }

interface Posted {
  inputs: SimInputs
  model: MarketModel
  paths: number
}

/**
 * Runs one projection per entry in a single Web Worker, off the main thread.
 *
 * `entries` is compared by the reference identity of each `inputs` and `model` —
 * memoise them in the caller, or every render will queue another simulation.
 *
 * ── Which entries re-run, and why it is not always one ──────────────────────
 * The rule is a consequence of the posting condition rather than a special case:
 *
 *  - an entry whose `inputs`/`model` changed posts at the current tier;
 *  - an entry whose last run was a PREVIEW posts again at full tier once the
 *    thumb lifts, because a preview is not a finished answer;
 *  - anything else is skipped.
 *
 * **This hook posts for whatever the caller hands it fresh, and that is not always
 * one entry.** The comment here used to say only the active scenario's inputs
 * change while a slider moves, so the cost of two fans equalled the cost of one.
 * That is true of the four money and inflation sliders and false of both age
 * sliders, measured over CDP by scenario key: `currentAge` propagates to every
 * plan (`updateActivePlan`), and the horizon is one per SET rather than per
 * scenario (`sharedHorizon`), so an age change genuinely re-mints both entries and
 * both post every frame. See the per-slider table in README's Performance section.
 *
 * The rule above is what makes that correct rather than wasteful — an untouched
 * entry keeps its object identity and is skipped, and when `interacting` flips
 * false a comparison whose last run was already full is skipped too. What varies
 * is how many entries arrive changed, which is the caller's business, not this
 * hook's.
 */
export function useScenarioSimulations(
  entries: SimEntry[],
  options: Options = {},
): Record<SimKey, SimulationState> {
  const { interacting = false, seed = SHARED_SEED } = options

  const workerRef = useRef<Worker | null>(null)
  const generationRef = useRef(new Map<SimKey, number>())
  const newestSeenRef = useRef(new Map<SimKey, number>())
  const postedRef = useRef(new Map<SimKey, Posted>())

  const [states, setStates] = useState<Record<SimKey, SimulationState>>({})
  /** Worker-level failure. Applies to every entry, including ones with no state yet. */
  const [fatal, setFatal] = useState<string | null>(null)

  useEffect(() => {
    const worker = new Worker(new URL('../worker/sim.worker.ts', import.meta.url), {
      type: 'module',
    })
    workerRef.current = worker

    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const data = event.data
      // A reply is proof the worker is alive, so any earlier fatal is stale.
      setFatal(null)
      // Drop anything older than what we have already rendered, per key.
      const newest = newestSeenRef.current.get(data.key) ?? -1
      if (data.generation < newest) return
      newestSeenRef.current.set(data.key, data.generation)

      setStates((prev) => {
        const previous = prev[data.key] ?? INITIAL
        if ('error' in data) {
          return { ...prev, [data.key]: { ...previous, error: data.error } }
        }
        return {
          ...prev,
          [data.key]: {
            result: data.result,
            isPreview: data.result.paths < FULL_PATHS,
            elapsedMs: data.elapsedMs,
            error: null,
          },
        }
      })
    }

    /*
     * A worker-level failure belongs to no key, so it cannot be written into the
     * per-key map — which is what the previous version tried to do, iterating
     * `Object.entries(prev)`. The map is `{}` at exactly the moment this fires
     * (module 404 after a deploy, CSP block, an in-app WebView without module
     * workers — Instagram's browser is the traffic this product is built for), so
     * the loop wrote nothing, no error surfaced, and the screen sat on "Running
     * simulation…" forever. On the single-series hook this rendered the alert.
     */
    worker.onerror = (event) => setFatal(event.message || 'Simulation failed')

    return () => {
      worker.terminate()
      workerRef.current = null
      // A remounted worker has no memory of what it already computed, so neither
      // may we — otherwise nothing would ever be posted again.
      postedRef.current.clear()
      newestSeenRef.current.clear()
    }
  }, [])

  useEffect(() => {
    const worker = workerRef.current
    if (worker === null) return

    /*
     * Drop every trace of a key that is no longer live — the posted record, the
     * generation counters AND the result itself.
     *
     * `states` was previously never pruned, and that is not merely untidy:
     * uids are not reused, but a key can still stop being live (dropping a
     * comparison), and leaving its result in the map keeps a deleted plan's
     * numbers reachable by anything that indexes the map. The share card is
     * built from whatever the active key resolves to, so a stale entry is a
     * publishable artefact carrying a plan the user threw away.
     *
     * The two generation maps go with it: keeping `newestSeen` for a dead key
     * while clearing its counter would make every future reply for that key
     * look stale and be dropped forever.
     */
    const live = new Set(entries.map((e) => e.key))
    for (const key of [...postedRef.current.keys()]) {
      if (!live.has(key)) postedRef.current.delete(key)
    }
    for (const key of [...generationRef.current.keys()]) {
      if (!live.has(key)) generationRef.current.delete(key)
    }
    for (const key of [...newestSeenRef.current.keys()]) {
      if (!live.has(key)) newestSeenRef.current.delete(key)
    }
    setStates((prev) => {
      const stale = Object.keys(prev).filter((key) => !live.has(key))
      if (stale.length === 0) return prev
      const next = { ...prev }
      for (const key of stale) delete next[key]
      return next
    })

    const paths = interacting ? PREVIEW_PATHS : FULL_PATHS
    for (const entry of entries) {
      const posted = postedRef.current.get(entry.key)
      const changed =
        posted === undefined || posted.inputs !== entry.inputs || posted.model !== entry.model
      // A preview is provisional; it has to be upgraded once the gesture ends.
      const needsSettle = posted !== undefined && posted.paths < FULL_PATHS && !interacting
      if (!changed && !needsSettle) continue

      const generation = (generationRef.current.get(entry.key) ?? 0) + 1
      generationRef.current.set(entry.key, generation)

      const request: SimRequest = {
        key: entry.key,
        generation,
        inputs: entry.inputs,
        model: entry.model,
        paths,
        seed,
      }
      // No debounce on purpose. The worker coalesces to the newest pending
      // request per key, so posting on every frame of a drag costs one
      // structured clone and never queues work we would have to wait through.
      worker.postMessage(request)
      postedRef.current.set(entry.key, { inputs: entry.inputs, model: entry.model, paths })
    }
  }, [entries, interacting, seed])

  /*
   * The returned view carries exactly the requested entries and nothing else, so
   * a removed scenario cannot leave its fan behind.
   *
   * The internal `states` map is pruned in the effect above when a key stops
   * being live, so nothing outlives the scenario it belongs to.
   */
  return useMemo(() => {
    const next: Record<SimKey, SimulationState> = {}
    for (const entry of entries) {
      const state = states[entry.key] ?? INITIAL
      next[entry.key] = fatal === null ? state : { ...state, error: fatal }
    }
    return next
  }, [entries, states, fatal])
}

/**
 * Single-series convenience wrapper. Used by the two dev routes, which have no
 * scenarios and no reason to know the plural form exists.
 */
export function useSimulation(
  inputs: SimInputs,
  model: MarketModel,
  options: Options = {},
): SimulationState {
  const entries = useMemo<SimEntry[]>(
    () => [{ key: SOLO_KEY, inputs, model }],
    [inputs, model],
  )
  const states = useScenarioSimulations(entries, options)
  return states[SOLO_KEY] ?? INITIAL
}

export type { SimResult }
